// src/components/common/PaginationFooter.jsx
import React from "react";
import { useTranslation } from "react-i18next";

export default function PaginationFooter({ page, size, total, onPageChange, onSizeChange }) {
    const { t } = useTranslation();
    const totalPages = Math.max(1, Math.ceil((total || 0) / (size || 10)));
    const startItem = total === 0 ? 0 : page * size + 1;
    const endItem = Math.min((page + 1) * size, total);

    const canGoPrev = page > 0;
    const canGoNext = page + 1 < totalPages;

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-2 bg-white border-t border-gray-200">
            <div className="text-sm text-gray-700">
                {t("common.showingRange", { start: startItem, end: endItem, total })}
            </div>

            <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                    <label htmlFor="page-size" className="text-sm font-medium text-gray-700">
                        {t("common.rowsPerPage")}
                    </label>
                    <select
                        id="page-size"
                        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                        value={size}
                        onChange={(e) => onSizeChange(Number(e.target.value))}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={!canGoPrev}
                        onClick={() => onPageChange(0)}
                        title={t("common.firstPage")}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                    </button>

                    <button
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={!canGoPrev}
                        onClick={() => onPageChange(Math.max(0, page - 1))}
                        title={t("common.previousPage")}
                    >
                        {t("common.prev")}
                    </button>

                    <span className="px-4 py-1.5 text-sm font-medium text-gray-700">
                        {t("common.pageOf", { current: page + 1, total: totalPages })}
                    </span>

                    <button
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={!canGoNext}
                        onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
                        title={t("common.nextPage")}
                    >
                        {t("common.next")}
                    </button>

                    <button
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={!canGoNext}
                        onClick={() => onPageChange(totalPages - 1)}
                        title={t("common.lastPage")}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
